import React from "react";

interface ComparablesTableSkeletonProps {
    requiredAmount?: number,
}

const ComparablesTableSkeletonRenderer: React.FC<ComparablesTableSkeletonProps> = ({
                                                                                      requiredAmount = 4,
                                                                                  }) => {
    // Default number of placeholder comparables to show
    const tableRowCount = 5;

    // Define the animation class that will be used for all skeleton elements
    const pulseClass = "animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:400%_100%]";

    return (
        <div className="space-y-6">
            {/* Selected Comparables Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className={`h-7 w-48 rounded ${pulseClass}`}></div>
                    <div className={`h-5 w-24 rounded ${pulseClass}`}></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Array.from({length: requiredAmount}).map((_, index) => (
                        <div key={`comp-skeleton-${index}`} className="border-2 border-dashed border-gray-200 bg-gray-50 rounded-lg p-4 h-40">
                            <div className="flex flex-col items-center justify-center h-full space-y-2">
                                <div className={`w-8 h-8 rounded-full ${pulseClass}`}></div>
                                <div className={`h-4 w-24 rounded ${pulseClass}`}></div>
                                <div className={`h-3 w-32 rounded ${pulseClass}`}></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Comparables Table Section */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 bg-gray-50">
                    <div className={`h-7 w-48 rounded mb-2 ${pulseClass}`}></div>
                    <div className={`h-5 w-64 rounded ${pulseClass}`}></div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                        <tr className="bg-gray-50">
                            {Array.from({length: 10}).map((_, index) => (
                                <th key={`header-cell-${index}`} className="px-6 py-3">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className={`h-4 w-16 rounded ${pulseClass}`}></div>
                                        <div className={`h-3 w-12 rounded ${pulseClass}`}></div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {Array.from({length: tableRowCount}).map((_, rowIndex) => (
                            <tr key={`table-row-${rowIndex}`} className="hover:bg-gray-50">
                                {/* ID Cell */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex items-center justify-center">
                                        <div className={`w-8 h-8 rounded-full ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* MLS Cell */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className={`h-5 w-16 rounded ${pulseClass}`}></div>
                                </td>

                                {/* Address Cell */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className={`h-5 w-32 rounded ${pulseClass}`}></div>
                                </td>

                                {/* Proximity/Property Type */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className={`h-5 w-16 rounded ${pulseClass}`}></div>
                                        <div className={`h-4 w-12 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* Status/Type */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-2">
                                        <div className={`h-6 w-16 rounded-full ${pulseClass}`}></div>
                                        <div className={`h-4 w-12 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* Date/Price */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className={`h-5 w-20 rounded ${pulseClass}`}></div>
                                        <div className={`h-5 w-16 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* Orig Price/DOM */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className={`h-5 w-20 rounded ${pulseClass}`}></div>
                                        <div className={`h-5 w-16 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* Rooms/Stories|Units */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className="flex justify-center space-x-1 mb-1">
                                            <div className={`h-6 w-8 rounded ${pulseClass}`}></div>
                                            <div className={`h-6 w-8 rounded ${pulseClass}`}></div>
                                            <div className={`h-6 w-8 rounded ${pulseClass}`}></div>
                                        </div>
                                        <div className={`h-4 w-16 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* GLA/Year Built */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className={`h-5 w-20 rounded ${pulseClass}`}></div>
                                        <div className={`h-4 w-12 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>

                                {/* Lot Size/Garage */}
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <div className="flex flex-col items-center space-y-1">
                                        <div className={`h-5 w-20 rounded ${pulseClass}`}></div>
                                        <div className={`h-4 w-12 rounded ${pulseClass}`}></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export const ComparablesTableSkeleton = React.memo(ComparablesTableSkeletonRenderer);